import { faSpinner, faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState, memo } from 'react'
import { useDebounce } from 'react-use'
import styled from 'styled-components'
import { ButtonVariants } from './buttonVariants'

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
`

const SearchWrapperIcons = styled.div`
  position: absolute;
  right: 10px;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: end;
`

interface InputSearchParams {
  placeholder?: string
  allData?: any[]
  setAllData?: Dispatch<SetStateAction<any[]>>
  objectField?: string[] | 'all'
  setIsDebouncing?: Dispatch<SetStateAction<boolean>>
  debounceTime?: number
  loadingWhenDebouncing?: boolean
  style?: React.CSSProperties
  className?: string
  onSearchChange?: (value: string) => void
}

const InputSearch = ({
  placeholder,
  allData,
  setAllData,
  objectField,
  setIsDebouncing,
  debounceTime,
  loadingWhenDebouncing,
  style,
  className,
  onSearchChange,
}: InputSearchParams) => {
  const [search, setSearch] = useState<string>('')
  const refInput = useRef<HTMLInputElement>(null)
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const isDebouncing = search !== debouncedSearch

  useEffect(() => {
    setIsDebouncing?.(isDebouncing)
  }, [isDebouncing, setIsDebouncing])

  const filterLocalData = useCallback(
    (inputValue: string) => {
      const filteredData = allData?.filter(item => {
        if (objectField === 'all') {
          return Object.values(item).some(value =>
            value?.toString().toLowerCase().includes(inputValue.toLowerCase()),
          )
        }

        return objectField?.some(field =>
          item[field]?.toString().toLowerCase().includes(inputValue.toLowerCase()),
        )
      })

      setAllData?.(filteredData || [])
    },
    [allData, objectField],
  )

  useDebounce(
    () => {
      filterLocalData(search)
      if (search !== debouncedSearch) {
        setDebouncedSearch(search)
        onSearchChange?.(search)
      }
    },
    debounceTime ?? 400,
    [search],
  )

  const handleCleanButton = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()

      setSearch('')
      setDebouncedSearch('')
      onSearchChange?.('')
      setAllData?.(allData || [])
      refInput.current?.focus()
    },
    [allData, setAllData, onSearchChange, refInput],
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value)
    },
    [setSearch],
  )

  return (
    <SearchWrapper>
      <SearchInput
        ref={refInput}
        type='text'
        placeholder={placeholder || 'Buscar'}
        value={search}
        onChange={e => {
          handleSearchChange(e.target.value)
        }}
        style={style}
        className={className}
      />

      <SearchWrapperIcons>
        {loadingWhenDebouncing && isDebouncing ? (
          <FontAwesomeIcon icon={faSpinner} spin width={20} height={20} />
        ) : null}
        {search.length > 0 && (
          <ButtonVariants
            onClick={handleCleanButton}
            icon={faXmark}
            variant='transparent'
            style={{ padding: 5 }}
          />
        )}
      </SearchWrapperIcons>
    </SearchWrapper>
  )
}

export default memo(InputSearch)
